/**
 * Dettaglio scontrino con righe e pagamenti.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { ArrowLeft } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

interface ScontrinoInfo {
  id: string;
  year: number;
  tipo: string;
  stato: string;
  subtotale: string;
  totale: string;
  sconto_totale: string;
  numero_scontrino_fiscale: number | null;
  numero_azzeramento: number | null;
  matricola_rt: string | null;
  data_emissione: string | null;
  ora_emissione: string | null;
  created_at: string;
}

interface Riga {
  id: string;
  descrizione: string;
  quantita: string;
  prezzo_unitario: string;
  importo_riga: string;
  importo_sconto: string;
  soglia_applicata: string;
  tipo_riga: string;
  aliquota_iva: string;
}

interface Pagamento {
  id: string;
  metodo: string;
  importo: string;
  importo_ricevuto: string | null;
  resto: string | null;
  riferimento: string | null;
}

const ScontrinoDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const year = Number(searchParams.get('year') ?? new Date().getFullYear());

  const [scontrino, setScontrino] = useState<ScontrinoInfo | null>(null);
  const [righe, setRighe] = useState<Riga[]>([]);
  const [pagamenti, setPagamenti] = useState<Pagamento[]>([]);
  const [loading, setLoading] = useState(true);

  const carica = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [resSc, resRighe, resPag] = await Promise.all([
        fetch(`/api/v1/cassa/scontrini/${id}?year=${year}`, { headers: authHeaders() }),
        fetch(`/api/v1/cassa/scontrini/${id}/righe?year=${year}`, { headers: authHeaders() }),
        fetch(`/api/v1/cassa/scontrini/${id}/pagamenti?year=${year}`, { headers: authHeaders() }),
      ]);
      if (resSc.ok) setScontrino(await resSc.json());
      if (resRighe.ok) setRighe(await resRighe.json());
      if (resPag.ok) setPagamenti(await resPag.json());
    } finally {
      setLoading(false);
    }
  }, [id, year]);

  useEffect(() => {
    void carica();
  }, [carica]);

  const formatCurrency = (value: string): string =>
    `${parseFloat(value).toFixed(2)} \u20AC`;

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Caricamento…</p>
      </div>
    );
  }

  if (!scontrino) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">Scontrino non trovato</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Scontrino {scontrino.numero_scontrino_fiscale ?? '(bozza)'}
        </h1>
        <Badge variant="outline">{scontrino.tipo}</Badge>
        <Badge>{scontrino.stato}</Badge>
      </div>

      {/* Info generali */}
      <Card>
        <CardHeader>
          <CardTitle>Dettagli</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <div>
              <p className="text-muted-foreground">N. Fiscale</p>
              <p className="font-mono">{scontrino.numero_scontrino_fiscale ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Azzeramento</p>
              <p className="font-mono">{scontrino.numero_azzeramento ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Matricola RT</p>
              <p className="font-mono">{scontrino.matricola_rt ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Data emissione</p>
              <p className="font-mono">{scontrino.data_emissione ?? '—'}</p>
            </div>
          </div>
          <div className="mt-4 flex gap-8 border-t border-border pt-4">
            <div>
              <p className="text-sm text-muted-foreground">Subtotale</p>
              <p className="font-mono text-lg">{formatCurrency(scontrino.subtotale)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Sconti</p>
              <p className="font-mono text-lg text-destructive">
                -{formatCurrency(scontrino.sconto_totale)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Totale</p>
              <p className="font-mono text-xl font-semibold">{formatCurrency(scontrino.totale)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Righe */}
      <Card>
        <CardHeader>
          <CardTitle>Righe</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Descrizione</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Qtà</TableHead>
                <TableHead className="text-right">Prezzo</TableHead>
                <TableHead className="text-right">Sconto</TableHead>
                <TableHead className="text-right">Importo</TableHead>
                <TableHead>IVA</TableHead>
                <TableHead>Soglia</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {righe.map((r: any) => (
                <TableRow key={r.id}>
                  <TableCell>{r.descrizione}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{r.tipo_riga}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">{r.quantita}</TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(r.prezzo_unitario)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {parseFloat(r.importo_sconto) > 0
                      ? `-${formatCurrency(r.importo_sconto)}`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(r.importo_riga)}
                  </TableCell>
                  <TableCell className="font-mono">{r.aliquota_iva}%</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        r.soglia_applicata === 'forzato'
                          ? 'destructive'
                          : r.soglia_applicata === 'minimo'
                          ? 'secondary'
                          : 'outline'
                      }
                    >
                      {r.soglia_applicata}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagamenti */}
      <Card>
        <CardHeader>
          <CardTitle>Pagamenti</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metodo</TableHead>
                <TableHead className="text-right">Importo</TableHead>
                <TableHead className="text-right">Ricevuto</TableHead>
                <TableHead className="text-right">Resto</TableHead>
                <TableHead>Riferimento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagamenti.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    Nessun pagamento registrato
                  </TableCell>
                </TableRow>
              )}
              {pagamenti.map((p: any) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <Badge variant="outline">{p.metodo}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(p.importo)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {p.importo_ricevuto ? formatCurrency(p.importo_ricevuto) : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {p.resto ? formatCurrency(p.resto) : '—'}
                  </TableCell>
                  <TableCell className="text-sm">{p.riferimento ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScontrinoDetail;
